import { PRODUCT_ENTITLEMENTS } from '@headline/schemas'
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router'
import { StreamConfigView } from '@/components/stream/StreamConfigView'
import { accountQuery, coverageListsQuery, subaccountsQuery, topicsQuery } from '@/lib/queries'
import { streamConfigQuery } from '@/lib/stream-queries'
import { primarySubaccount } from '@/lib/stream-workspace'

/**
 * The account workspace's stream configuration — one page for everything the stream is. Topic rows
 * span the whole account TREE (every sub-account's topics, owner shown), because attaching a
 * client's topic is how the account stream inherits client data. Gated like the pages it
 * configures: entitled to Library OR Analytics, else no configure surface either.
 */
export const Route = createFileRoute('/app/accounts/$accountId_/stream-config')({
  loader: async ({ context, params }) => {
    const account = await context.queryClient.ensureQueryData(accountQuery(params.accountId))
    const entitled =
      account.entitlements.includes(PRODUCT_ENTITLEMENTS.library) ||
      account.entitlements.includes(PRODUCT_ENTITLEMENTS.analytics)
    if (!entitled) {
      throw redirect({ to: '/app/accounts/$accountId', params: { accountId: params.accountId } })
    }
    const [subs] = await Promise.all([
      context.queryClient.ensureQueryData(subaccountsQuery(params.accountId)),
      context.queryClient.ensureQueryData(coverageListsQuery(params.accountId)),
    ])
    await Promise.all([
      ...subs.subaccounts.map((sub) => context.queryClient.ensureQueryData(topicsQuery(sub.id))),
      context.queryClient.ensureQueryData(
        streamConfigQuery(primarySubaccount(subs.subaccounts).id),
      ),
    ])
  },
  component: AccountStreamConfigPage,
})

function AccountStreamConfigPage() {
  const { accountId } = Route.useParams()
  const navigate = useNavigate()
  const router = useRouter()
  const { data: account } = useSuspenseQuery(accountQuery(accountId))
  const { data: subsData } = useSuspenseQuery(subaccountsQuery(accountId))
  const { data: listsData } = useSuspenseQuery(coverageListsQuery(accountId))
  const topicResults = useSuspenseQueries({
    queries: subsData.subaccounts.map((sub) => topicsQuery(sub.id)),
  })

  const groups = subsData.subaccounts.map((sub, index) => ({
    subaccountId: sub.id,
    owner: sub.name,
    isPrimary: sub.isPrimary,
    topics: topicResults[index]?.data.topics ?? [],
  }))

  return (
    <StreamConfigView
      workspaceName={account.name}
      subaccountId={primarySubaccount(subsData.subaccounts).id}
      accountId={accountId}
      groups={groups}
      lists={listsData.lists}
      subaccounts={subsData.subaccounts}
      onBack={() => {
        if (router.history.canGoBack()) router.history.back()
        else void navigate({ to: '/app/accounts/$accountId/library', params: { accountId } })
      }}
    />
  )
}
